import { individual_header_style } from 'assets/styles/table_custom_style';
import { icon_column_style } from 'assets/styles/table_custom_style';
import { individual_icon_header_style } from 'assets/styles/table_custom_style';
import { individual_column_style } from 'assets/styles/table_custom_style';
import ToggleSwitch from 'components/ToggleSwitch/ToggleSwitch';
import { Button, Row } from 'reactstrap';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import priceCatalogService from 'views/CRM/PriceCatalog/service/priceCatalogService';
import ReactLoader from 'components/Loader';
import DiscountMatrixTable from '../DiscountMatrixTable';
import { toast } from 'react-toastify';
import { useDiscountsMatrix } from 'hooks/useDiscountsMatrix';

const UpdatePriceCatalog = ({
  catalogId,
  onUpdatedProducts,
  onUpdatedDiscountMatrix
}) => {
  // State Hooks
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const {
    discounts: discountMatrix,
    setDiscounts: setDiscountMatrix,
    handleAddDiscount,
    handleRemoveDiscount,
    handleDiscountChange
  } = useDiscountsMatrix([]);

  // Effect Hook
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const [productsResponse, discountResponse] = await Promise.allSettled([
        priceCatalogService.getProductsByPriceCatalogId(catalogId),
        priceCatalogService.getDiscountMatrixByCatalogId(catalogId)
      ]);

      if (productsResponse.status !== 'fulfilled') {
        console.error('Error fetching products:', productsResponse.reason);
        return;
      }

      const productsWithEditState = productsResponse.value.data.data.map(
        (product) => ({
          ...product,
          isEditing: false
        })
      );
      setProducts(productsWithEditState);

      if (discountResponse.status !== 'fulfilled') {
        console.error('Error fetching discounts:', discountResponse.reason);
        return;
      }
      const parsedDiscount = discountResponse.value.data.data.map(
        (discount) => {
          // eslint-disable-next-line no-unused-vars
          const { id, ...rest } = discount;
          return {
            ...rest,
            discount: parseFloat(discount.discount)
          };
        }
      );
      setDiscountMatrix(parsedDiscount);
      setLoading(false);
    };

    if (catalogId) {
      fetchData();
    }
  }, [catalogId]);

  useEffect(() => {
    onUpdatedProducts(products);
  }, [products]);

  // Event Handlers
  const handleEditClick = (row) => {
    const { id } = row;

    if (!id) {
      console.warn('Id não fornecido!');
      return;
    }

    setProducts((prev) =>
      prev.map((product) => ({
        ...product,
        isEditing: product.id === id && !product.isEditing
      }))
    );
  };

  const handlePriceChange = (productId, newValue) => {
    const productToUpdate = products.find(
      (product) => product.id === productId
    );

    if (productToUpdate) {
      productToUpdate.catalogPrice = newValue;

      setProducts((prev) =>
        prev.map((product) =>
          product.id === productId ? productToUpdate : product
        )
      );
    }
  };

  const handleToggleProductStatusInPriceCatalog = useCallback(
    async (catalogId, productId, data) => {
      await priceCatalogService
        .enableDisableProductInPriceCatalog(catalogId, productId, data)
        .then(() => {
          toast.success(
            `Produto ${
              !data?.isActive ? 'habilitado' : 'desabilitado'
            } com sucesso`,
            {
              autoClose: 3000,
              closeOnClick: true
            }
          );
        })
        .catch((error) => {
          const msg = error?.response?.data?.message;
          toast.error(
            msg ||
              `Erro ao ${!data?.enabled ? 'habilitar' : 'desabilitar'} Produto`,
            {
              autoClose: 3000,
              closeOnClick: true
            }
          );
        });
    },
    []
  );

  const toggleProductStatusInPriceCatalog = useCallback(
    ({ catalogId, productId, isActive }) => {
      const data = { isActive };

      try {
        handleToggleProductStatusInPriceCatalog(catalogId, productId, data);

        setProducts((prevProducts) =>
          prevProducts.map((product) =>
            product.id === productId
              ? { ...product, isActive: !isActive }
              : product
          )
        );
      } catch (error) {
        const msg = error?.response?.data?.message;
        toast.error(
          msg || `Erro ao ${!isActive ? 'desabilitar' : 'habilitar'} o Produto`
        );
        setProducts((prevProducts) => prevProducts);
      }
    },
    [handleToggleProductStatusInPriceCatalog]
  );

  // Columns Definition
  const columns = useMemo(
    () => [
      {
        dataField: 'name',
        text: 'PRODUTO',
        sort: true,
        style: individual_column_style,
        headerStyle: individual_header_style
      },
      {
        dataField: 'basePrice',
        text: 'VALOR PADRÃO',
        sort: true,
        style: individual_column_style,
        headerStyle: individual_header_style
      },
      {
        dataField: 'catalogPrice',
        text: 'VALOR NO CATÁLOGO',
        sort: true,
        style: individual_column_style,
        headerStyle: individual_header_style,
        formatter: (cell, row) => {
          return row.isEditing ? (
            <input
              type="number"
              min="0"
              value={row.catalogPrice}
              onChange={(e) => handlePriceChange(row.id, e.target.value)}
              ref={(node) => {
                if (node) {
                  return node.focus();
                }
              }}
              className="form-control form-control-sm"
              placeholder={cell}
            />
          ) : (
            row.catalogPrice
          );
        }
      },
      {
        dataField: 'edit',
        text: 'EDITAR',
        style: icon_column_style,
        headerStyle: individual_icon_header_style,
        formatter: (_, row) => {
          const isEditing = row.isEditing;
          return (
            <Button
              color={isEditing ? 'success' : 'info'}
              size="sm"
              onClick={() => handleEditClick(row)}
            >
              <i className={`fas fa-${isEditing ? 'check' : 'user-edit'}`}></i>
            </Button>
          );
        }
      },
      {
        dataField: 'habilitar/desabilitar',
        text: 'HABILITAR/DESABILITAR',
        style: icon_column_style,
        headerStyle: individual_icon_header_style,
        formatter: (_, row) => {
          return (
            <ToggleSwitch
              checked={row.isActive}
              id={row.id}
              onChange={() =>
                toggleProductStatusInPriceCatalog({
                  catalogId,
                  productId: row.id,
                  isActive: row.isActive
                })
              }
            />
          );
        }
      }
    ],
    [products, toggleProductStatusInPriceCatalog]
  );

  // Render Logic
  return (
    <>
      <ToolkitProvider
        data={products}
        keyField="id"
        columns={columns}
        bootstrap4={true}
      >
        {(props) => (
          <div className="py-4 table-responsive">
            {loading ? (
              <Row className="justify-content-md-center">
                <ReactLoader />
              </Row>
            ) : (
              <>
                <div className="mt-4">
                  <h4>Matriz de Desconto</h4>
                  <DiscountMatrixTable
                    discounts={discountMatrix}
                    onAddDiscount={handleAddDiscount}
                    onRemoveDiscount={handleRemoveDiscount}
                    onDiscountChange={handleDiscountChange}
                    onUpdatedDiscountMatrix={onUpdatedDiscountMatrix}
                  />
                </div>
                <div className="mt-4">
                  <h4>Produtos Cadastrados</h4>
                  <BootstrapTable
                    {...props.baseProps}
                    bootstrap4={true}
                    bordered={false}
                    wrapperClasses="table-responsive"
                    classes="align-items-center table"
                    style={{ width: '100%' }}
                    noDataIndication="Nenhum produto encontrado"
                  />
                </div>
              </>
            )}
          </div>
        )}
      </ToolkitProvider>
    </>
  );
};

export default UpdatePriceCatalog;
